import GridWindow from "components/GridWindow";
import styles from "./styles.module.scss";
import NoteIcon from "assets/icons/note.svg?react";
import { FC, Props } from "./typings";
import NotesService from "api/NotesService";
import moment from "moment";
import { useAuth } from "react-oidc-context";
import { getUserId } from "utils/getUserId";
import { decryptPackage } from "utils/decryptPackage";
import { useEffect, useRef, useState } from "react";
import AddNote from "components/AddNote";
import decryptNoteFields, { INoteContentSource } from "utils/decryptNoteFields";
import BinIcon from "assets/icons/bin.svg?react";
import Loading from "components/Loading";
import NotFoundScreen from "components/NotFoundScreen";
import { randomString } from "utils/randomString";
import { useTranslation } from "react-i18next";
export interface INote extends INoteContentSource {
  Id: string;
  CreationDate: string;
  Title: string;
  Content: string;
  Kind: number;
  Case: any;
  LastModification: string;
  decoded?: boolean;
}

const NotiBox = ({
  note,
  onClick,
  handleDelete,
  listRef,
  n,
  setN,
}: {
  note: INote;
  n: INote[];
  setN: (notes: INote[]) => void;
  listRef: any;
  onClick: (note: INote, title: string, content: string) => void;
  handleDelete: (note: INote) => void;
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const userId = getUserId(auth.user!);

  const [hover, setHover] = useState(false);

  const notiBoxRef = useRef(null);

  const [startedDecode, setStartedDecode] = useState(false);

  const handleDecode = async () => {
    setStartedDecode(true);
    if (!note.decoded) {
      const copy = [...n];

      const element = copy.find((a) => a.Id === note.Id);
      if (!element) return;

      try {
        // Notatka z EncryptionKeyId (po F3): treść szyfruje klucz OBIEKTU owinięty kluczem
        // firmy — ścieżka dwustopniowa. Bez EncryptionKeyId to wiersz SPRZED migracji:
        // purge F4 NIE został uruchomiony na produkcji, więc takie notatki wciąż istnieją,
        // a jedyne, czym da się je otworzyć, to zestaw kluczy składany przez decryptPackage
        // (mirror treści + klucz pośredni). Tak samo robi
        // components/Registry/KSeFInvoiceRegister/NotesModal — trzecie miejsce z notatkami.
        const fields = note.EncryptionKeyId
          ? await decryptNoteFields(note, [])
          : {
              title: note.Title ? await decryptPackage(userId, note.Title) : "",
              content: note.Content
                ? await decryptPackage(userId, note.Content)
                : "",
            };

        element.Title = fields.title;
        element.Content = fields.content;
        element.decoded = true;

        setN(copy);
      } catch (e) {
        // Notatka może być nieodszyfrowywalna (szkic/niepoprawny szyfrogram, np. treść
        // "Brudnopis", albo niepasujący klucz). handleDecode jest wołane fire-and-forget
        // przy scrollu, więc bez try/catch leciał unhandled rejection "Failed to fetch"
        // (Sentry #740). Pomijamy taką notatkę zamiast wywalać cały widok.
        console.warn("Nie udało się odszyfrować notatki", note.Id, e);
      }
    }
  };

  useEffect(() => {
    if (!listRef.current) return;

    const handleScroll = () => {
      if (notiBoxRef.current) {
        //@ts-ignore
        const boundingBox = notiBoxRef.current.getBoundingClientRect();
        // Sprawdź, czy NotiBox jest w widoku poziomym i pionowym
        if (
          boundingBox.top >= 0 &&
          boundingBox.left >= 0 &&
          boundingBox.bottom <=
            (window.innerHeight || document.documentElement.clientHeight) &&
          boundingBox.right <=
            (window.innerWidth || document.documentElement.clientWidth)
        ) {
          // setIsVisible(true);
          if (startedDecode) return;
          if (note.decoded) return;
          handleDecode();
        }
      }
    };

    // Dodaj nasłuchiwanie na zdarzenie scrollowania
    listRef?.current?.addEventListener("scroll", handleScroll);

    // Sprawdź widoczność NotiBox na starcie (może być widoczny domyślnie)
    handleScroll();

    // Usuń nasłuchiwanie zdarzenia scrollowania po wyjściu z komponentu
    return () => {
      listRef?.current?.removeEventListener("scroll", handleScroll);
    };
  }, [listRef, listRef?.current, notiBoxRef, notiBoxRef?.current, note]);

  return (
    <div
      ref={notiBoxRef}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => {
        onClick(note, note.Title, note.Content);
      }}
      className={styles.box}
    >
      {hover && (
        <BinIcon
          onClick={(e) => {
            handleDelete(note);
            e.preventDefault();
            e.stopPropagation();
          }}
          className={styles.remove}
        />
      )}
      <div className={styles.wrapBox}>
        <div className={styles.data}>
          <div className={styles.title}>
            <div
              dangerouslySetInnerHTML={{
                __html: note.decoded ? note.Title : t("common.loading"),
              }}
              className={styles.left}
            ></div>
            <div className={styles.right}>
              {moment(note.CreationDate).format("DD.MM.YY")}
            </div>
          </div>
          <div
            dangerouslySetInnerHTML={{
              __html: note.decoded ? note.Content : t("common.loading"),
            }}
            className={styles.description}
          ></div>
        </div>
      </div>
    </div>
  );
};

const NotesDesk: FC<Props> = ({
  inGrid = true,
  showHeader = true,
  caseId,
  participantId,
  showCreateNoteExternal,
  setShowCreateNoteExternal,
}) => {
  const { t } = useTranslation();
  const {
    data: notes,
    refetch,
    isFetched,
  } = NotesService.useGetNotes(caseId, participantId);
  const { mutate: deleteNote } = NotesService.useDeleteNote();

  const [showCreate, setShowCreate] = useState(false);

  const [editNote, setEditNote] = useState<INote | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [content, setContent] = useState<string | null>(null);

  const [n, setN] = useState<INote[]>([]);

  const [loading] = useState(false);

  useEffect(() => {
    if (notes?.Items) {
      setN(() => notes?.Items);
    }
  }, [notes]);

  // useEffect(() => {
  //   return () => {
  //     setN([]);
  //   };
  // }, []);

  useEffect(() => {
    if (isFetched) {
      setN([]);
      refetch();
    }
  }, [caseId, participantId]);

  const reload = () => {
    if (isFetched) {
      //setN([]);
      refetch();
    }
  };

  const addNote = async (newN: INote) => {
    try {
      setN([newN, ...n]);
    } catch {
      // intentionally empty
    }
  };

  const handleDeleteNote = (note: INote) => {
    deleteNote(
      {
        Id: note.Id,
      },
      {
        onSuccess: () => {
          setN(n.filter((a) => a.Id !== note.Id));
        },
      },
    );
  };

  const listRef = useRef(null);

  const contentBlock = () => {
    return (
      <>
        <div ref={listRef} className={styles.wrapper}>
          {loading && <Loading />}
          {!loading && n.length === 0 && (
            <div className={styles.empty}>
              <NotFoundScreen
                size="small"
                title={t("notes.noNotes")}
                text={t("notes.addFirst")}
                buttonName={t("notes.newNote")}
                onClick={() => setShowCreate(true)}
              />
            </div>
          )}
          {!loading &&
            n.map((note: INote) => {
              return (
                <NotiBox
                  listRef={listRef}
                  handleDelete={handleDeleteNote}
                  key={note.Id + randomString()}
                  onClick={(note, title, content) => {
                    setEditNote(note);
                    setTitle(title);
                    setContent(content);
                    setShowCreate(true);
                  }}
                  note={note}
                  n={n}
                  setN={setN}
                />
              );
            })}
        </div>
        {/* <AddButton
            className={styles.add}
            onClick={() => setShowCreate(true)}
            text="Utwórz notatkę"
          /> */}
      </>
    );
  };

  return (
    <>
      {inGrid && (
        <GridWindow
          onCreate={() => setShowCreate(true)}
          createText={t("notes.createNew")}
          title={
            showHeader ? (
              <>
                <NoteIcon /> {t("notes.title")}
              </>
            ) : (
              <></>
            )
          }
        >
          {contentBlock()}
        </GridWindow>
      )}
      {!inGrid && contentBlock()}
      {(showCreate || showCreateNoteExternal) && (
        <AddNote
          caseId={caseId}
          participantId={participantId}
          defaultValues={
            editNote ? { id: editNote.Id, title, content } : undefined
          }
          refetch={reload}
          addNote={addNote}
          toggle={() => {
            setShowCreate(false);
            if (setShowCreateNoteExternal) {
              setShowCreateNoteExternal(false);
            }
            setEditNote(null);
            setTitle(null);
            setContent(null);
          }}
        />
      )}
    </>
  );
};

export default NotesDesk;
